console.log('🚀 [Dashboard.jsx] Ładowanie Dashboard');

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useDialog } from './Dialog';
import { DashboardSkeleton } from './Skeleton';
import { Breadcrumbs } from './Breadcrumbs';
import { AnimatedCounter } from './AnimatedCounter';

console.log('✅ [Dashboard.jsx] Importy załadowane');

function Dashboard() {
  console.log('🎨 [Dashboard.jsx] Renderowanie Dashboard');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [blacklist, setBlacklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBlacklistAlert, setShowBlacklistAlert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { unit } = useParams();
  const base = `/${unit}`;
  const { success, error } = useToast();
  const { confirm } = useDialog();
  const location = useLocation();
  
  useEffect(() => {
    loadUserData();
    
    // Clean up URL if there are query params (e.g., ?code=... from OAuth)
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [unit]);
  
  const loadUserData = async () => {
    try {
      const data = await api.get(`/api-me?unit=${unit}`);
      setUser(data.user);
      setStats(data.stats);
      setBlacklist(data.blacklist);
      setIsAdmin(data.isAdmin || false);
      if (data.blacklist) {
        setShowBlacklistAlert(true);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      if (error.message.includes('401')) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/');
  };
  
  const handleExportData = async () => {
    try {
      const blob = await api.getBlob('/export-me');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_data_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export data:', err);
      error('Nie udało się wyeksportować danych');
    }
  };
  
  const handleRequestErasure = async () => {
    const confirmed = await confirm(
      'Czy na pewno chcesz usunąć swoje dane? Ta operacja jest nieodwracalna!',
      'Usuń moje dane'
    );
    if (!confirmed) return;
    
    try {
      await api.post('/request-erasure');
      success('Twoje dane zostały usunięte');
      handleLogout();
    } catch (err) {
      console.error('Failed to request erasure:', err);
      error(err.message || 'Nie udało się usunąć danych');
    }
  };
  
  if (loading) {
    return <DashboardSkeleton />;
  }
  
  return (
    <div className="panel-screen">
      {/* Header */}
      <header className="panel-header-bar">
        <div className="panel-header-inner">
          <div className="logo">
            <img 
              src="https://cdn.discordapp.com/icons/1423017948582252686/bddd09dc3593933ee012741c09ccf6af.webp?size=128" 
              alt="Logo" 
              className="logo__img"
            />
            <span className="logo__text">San Andreas<span className="logo__dot">.</span>National Guard</span>
          </div>
          
          <div className="user-menu">
            <div className="user-trigger" onClick={handleLogout} role="button" tabIndex={0}>
              <span className="user-trigger__name">{user.username}</span>
              {isAdmin && <span className="user-trigger__tag">ADMIN</span>}
              <img 
                src={user.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'} 
                alt="Avatar" 
                className="user-trigger__avatar"
              />
              <svg className="user-trigger__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </header>

      {/* Panel Layout */}
      <div className="panel-layout">
        {/* Sidebar */}
        <aside className="panel-sidebar">
          <nav className="panel-nav">
            <Link to={`${base}/dashboard`} className="panel-nav__item active">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>Dashboard</span>
            </Link>
            {blacklist && (blacklist.scope === 'both' || blacklist.scope === 'application') ? (
              <span className="panel-nav__item panel-nav__item--disabled" title="Jesteś na blackliście – nie możesz składać podań">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <span>Złóż Podanie</span>
              </span>
            ) : (
              <Link to={`${base}/application`} className="panel-nav__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <span>Złóż Podanie</span>
              </Link>
            )}
            {blacklist && (blacklist.scope === 'both' || blacklist.scope === 'complaint') ? (
              <span className="panel-nav__item panel-nav__item--disabled" title="Jesteś na blackliście – nie możesz składać skarg">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Złóż Skargę</span>
              </span>
            ) : (
              <Link to={`${base}/complaint`} className="panel-nav__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Złóż Skargę</span>
              </Link>
            )}
            {isAdmin && (
              <Link to={`${base}/admin`} className="panel-nav__item panel-nav__item--admin">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                <span>Panel Admina</span>
              </Link>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="panel-content">
          <div className="panel-section">
            <div className="panel-section-header">
              <h1 className="panel-title">Dashboard</h1>
              <p className="panel-subtitle">Przegląd aktywności i statystyk</p>
            </div>

            {blacklist && showBlacklistAlert && (
              <div className="alert alert-danger blacklist-dismissable">
                <button 
                  className="alert-close" 
                  onClick={() => setShowBlacklistAlert(false)}
                  aria-label="Zamknij"
                >
                  ✕
                </button>
                <h3>⚠️ Jesteś na blackliście</h3>
                <p><strong>Zakres:</strong> {
                  blacklist.scope === 'application' ? '📋 Podania' :
                  blacklist.scope === 'complaint' ? '⚖️ Skargi' :
                  '🚧 Podania i Skargi'
                }</p>
                <p><strong>Powód:</strong> {blacklist.reason}</p>
                {blacklist.expires_at && (
                  <p><strong>Wygasa:</strong> {new Date(blacklist.expires_at).toLocaleString('pl-PL')}</p>
                )}
              </div>
            )}

            {/* Stats Grid */}
            <div className="stats-grid-modern">
              <div className="stat-card-modern">
                <div className="stat-card__icon stat-card__icon--primary">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div className="stat-card__content">
                  <div className="stat-card__label">Twoje Podania</div>
                  <div className="stat-card__value"><AnimatedCounter value={stats.applications} delay={100} /></div>
                </div>
              </div>

              <div className="stat-card-modern">
                <div className="stat-card__icon stat-card__icon--success">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                </div>
                <div className="stat-card__content">
                  <div className="stat-card__label">Przyjęte</div>
                  <div className="stat-card__value">{stats.applications_accepted}</div>
                </div>
              </div>

              <div className="stat-card-modern">
                <div className="stat-card__icon stat-card__icon--warning">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div className="stat-card__content">
                  <div className="stat-card__label">Twoje Skargi</div>
                  <div className="stat-card__value"><AnimatedCounter value={stats.complaints} delay={200} /></div>
                </div>
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="content-card">
              <div className="content-card__header">
                <h3 className="content-card__title">Szczegóły Podań</h3>
                <p className="content-card__desc">Status Twoich podań rekrutacyjnych</p>
              </div>
              <div className="stats-breakdown-grid">
                <div className="stat-mini stat-mini--success">
                  <span className="stat-mini__label">Przyjęte</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.applications_accepted} delay={250} /></span>
                </div>
                <div className="stat-mini stat-mini--danger">
                  <span className="stat-mini__label">Odrzucone</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.applications_rejected} delay={300} /></span>
                </div>
                <div className="stat-mini stat-mini--warning">
                  <span className="stat-mini__label">Oczekujące</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.applications_pending} delay={350} /></span>
                </div>
              </div>
              {stats.last_application && (
                <div className="info-text">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Ostatnie podanie: {new Date(stats.last_application).toLocaleDateString('pl-PL')}
                </div>
              )}
            </div>

            <div className="content-card">
              <div className="content-card__header">
                <h3 className="content-card__title">Szczegóły Skarg</h3>
                <p className="content-card__desc">Status Twoich złożonych skarg</p>
              </div>
              <div className="stats-breakdown-grid">
                <div className="stat-mini stat-mini--success">
                  <span className="stat-mini__label">Rozwiązane</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.complaints_resolved} delay={400} /></span>
                </div>
                <div className="stat-mini stat-mini--danger">
                  <span className="stat-mini__label">Odrzucone</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.complaints_dismissed} delay={450} /></span>
                </div>
                <div className="stat-mini stat-mini--warning">
                  <span className="stat-mini__label">Oczekujące</span>
                  <span className="stat-mini__value"><AnimatedCounter value={stats.complaints_pending} delay={500} /></span>
                </div>
              </div>
              {stats.last_complaint && (
                <div className="info-text">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Ostatnia skarga: {new Date(stats.last_complaint).toLocaleDateString('pl-PL')}
                </div>
              )}
            </div>

            {/* GDPR Section */}
            <div className="content-card">
              <div className="content-card__header">
                <h3 className="content-card__title">Zarządzanie Danymi (RODO)</h3>
                <p className="content-card__desc">Masz prawo do eksportu i usunięcia swoich danych</p>
              </div>
              <div className="gdpr-actions">
                <button onClick={handleExportData} className="btn btn--secondary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Eksportuj dane
                </button>
                <button onClick={handleRequestErasure} className="btn btn--danger">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Usuń dane
                </button>
              </div>
              <div className="info-text">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Usunięcie danych jest możliwe po upływie okresu retencji (365 dni)
              </div>
            </div>

            <div className="credit">
              Created by <a href="https://herbatniczek.online/bio">herbatniczek0</a>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
